/**
 * CharacterCatalog — the roster view (U2.5).
 *
 * The showpiece read view: 36 seeded characters. Rich prose fields
 * (appearance, personality, dossier) are thin shells until the U3 Character
 * Creator authoring loop fills them — so this view leans on the *structural*
 * canon that IS seeded: role, alignment, and the resolved race + homeworld
 * names (raceId → race, homeworldId → world). That cross-resolution is what
 * makes the roster feel connected rather than a wall of blanks.
 *
 * Reuses the shared `.race-*` catalog layout classes.
 */

import { useEffect, useMemo, useState } from "react";

import {
  fetchCharacters,
  fetchRaces,
  fetchWorlds,
  type CharacterDTO,
  type RaceDTO,
  type WorldDTO,
} from "../api.js";

type LoadState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | {
      kind: "ready";
      characters: CharacterDTO[];
      raceNames: Map<string, string>;
      worldNames: Map<string, string>;
    };

const ROLE_ORDER: Record<string, number> = {
  main: 0,
  supporting: 1,
  antagonist: 2,
  wildcard: 3,
  foundational: 4,
  background: 5,
};

export function CharacterCatalog() {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchCharacters(), fetchRaces(), fetchWorlds()])
      .then(([characters, races, worlds]) => {
        if (cancelled) return;
        const raceNames = new Map(races.map((r: RaceDTO) => [r.id, r.name]));
        const worldNames = new Map(worlds.map((w: WorldDTO) => [w.id, w.name]));
        const sorted = [...characters].sort((a, b) => {
          const ra = ROLE_ORDER[a.role] ?? 9;
          const rb = ROLE_ORDER[b.role] ?? 9;
          return ra !== rb ? ra - rb : a.name.localeCompare(b.name);
        });
        setState({ kind: "ready", characters: sorted, raceNames, worldNames });
        if (sorted.length > 0) setSelectedId(sorted[0].id);
      })
      .catch((err: Error) => {
        if (!cancelled) setState({ kind: "error", message: err.message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = useMemo(() => {
    if (state.kind !== "ready") return null;
    return state.characters.find((c) => c.id === selectedId) ?? null;
  }, [state, selectedId]);

  if (state.kind === "loading") return <div className="placeholder">Loading characters…</div>;
  if (state.kind === "error") {
    return (
      <div className="placeholder" style={{ color: "#ef4444" }}>
        <strong>Failed to load characters:</strong> {state.message}
      </div>
    );
  }

  return (
    <div className="race-catalog">
      <aside className="race-list">
        <div className="race-list-header">
          <h2>Characters</h2>
          <span className="count">{state.characters.length}</span>
        </div>
        <ul>
          {state.characters.map((c) => (
            <li key={c.id}>
              <button
                className={`race-list-item ${c.id === selectedId ? "active" : ""}`}
                onClick={() => setSelectedId(c.id)}
              >
                <span className="name">{c.name}</span>
                <span className={`role-tag role-${c.role}`}>{c.role}</span>
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <section className="race-detail">
        {selected ? (
          <CharacterDetail
            character={selected}
            raceNames={state.raceNames}
            worldNames={state.worldNames}
          />
        ) : (
          <div className="placeholder">Pick a character.</div>
        )}
      </section>
    </div>
  );
}

function CharacterDetail({
  character,
  raceNames,
  worldNames,
}: {
  character: CharacterDTO;
  raceNames: Map<string, string>;
  worldNames: Map<string, string>;
}) {
  const race = character.raceId ? (raceNames.get(character.raceId) ?? "Unknown race") : "—";
  const home = character.homeworldId
    ? (worldNames.get(character.homeworldId) ?? "Unknown world")
    : "—";

  return (
    <article>
      <header className="detail-header">
        <h2>{character.name}</h2>
        <span className={`role-tag role-${character.role}`}>{character.role}</span>
      </header>

      {character.phoneticName && <p className="phonetic">{character.phoneticName}</p>}

      <dl className="detail-grid">
        <dt>Race</dt>
        <dd>{race}</dd>
        <dt>Homeworld</dt>
        <dd>{home}</dd>
        <dt>Alignment</dt>
        <dd>{character.alignment ?? <span className="empty">—</span>}</dd>
        <dt>Story arc</dt>
        <dd>{character.storyArc}</dd>
        <dt>Author</dt>
        <dd>{character.authorCredit === "alex_riley" ? "Alex Riley" : "Shykem"}</dd>
      </dl>

      {character.status.length > 0 && (
        <section>
          <h3>Status</h3>
          {character.status.map((s, i) => (
            <p key={i} className="tiered">
              {s.text}
            </p>
          ))}
        </section>
      )}

      {character.appearance.length > 0 && (
        <section>
          <h3>Appearance</h3>
          {character.appearance.map((s, i) => (
            <p key={i} className="tiered">
              {s.text}
            </p>
          ))}
        </section>
      )}

      {character.personality.length > 0 && (
        <section>
          <h3>Personality</h3>
          {character.personality.map((s, i) => (
            <p key={i} className="tiered">
              {s.text}
            </p>
          ))}
        </section>
      )}

      {character.status.length === 0 &&
        character.appearance.length === 0 &&
        character.personality.length === 0 && (
          <p className="dossier-pending">
            Full dossier (appearance, personality, forms, relationships) is authored in the
            Character Creator — coming in U3.
          </p>
        )}
    </article>
  );
}
